import { DateTime } from 'luxon';
import { z } from 'zod';
import { InputCardOption } from '~/components/input/InputCards';
import { capitalize } from '~/lib/format';
import { DistanceAddress, DistanceAddressSchema } from '~/lib/model';
import { coerceBoolean, ZodCoerceBoolean } from '~/lib/zod';
import { BRE_DELIVERY_FEES, BRE_HOLIDAYS, BrePrintSpeed } from '~/tenants/bre/data';
import { BreOrderType } from '~/tenants/bre/model/enums';
import { holiday_lookup } from '~lib/common/holidays/holidays';
import { BreOrderContext } from '~/tenants/bre/model/BreOrderContext';
import { datetime } from '~/lib/datettime';
import Big from 'big.js';

export const CONTENT_WRITING_PAY = '50';

export const CONTENT_WRITING_RATE = '75';

export const voiceSchema = z.enum(['male', 'female']);

export function consentSchema(message?: string): z.ZodEffects<z.ZodOptional<ZodCoerceBoolean>, boolean | undefined> {
  return coerceBoolean()
    .optional()
    .refine((v) => v === true, {
      message: message ?? 'Please read and accept all consents',
    });
}

export function deliverySchema(message?: string): z.ZodEffects<typeof DistanceAddressSchema, DistanceAddress> {
  return DistanceAddressSchema.refine(
    ({ time }) => time && deliveryFee(time, 'rate'),
    message ?? "We can't deliver that far; please contact the office.",
  );
}

export const VOICE_OPTS = voiceSchema.options.map<InputCardOption>((value) => ({
  title: `${capitalize(value)} Voice`,
  description: `We narrate with a ${value} voice`,
  value,
}));

export function bre_print_earliest(order: BreOrderContext, days: number): DateTime {
  for (const appointment of order.appointments) {
    const photos = order.jobs.find((j) => appointment.job_ids.includes(j.id) && j.performable_id === 'photos');

    if (photos) {
      return datetime(appointment.start, order.timezone).plus({ days }).startOf('day');
    }
  }

  if (order.requested.length > 0) {
    let earliest = datetime(order.requested[0].day, order.timezone);

    for (const requested of order.requested) {
      const day = datetime(requested.day, order.timezone);

      if (day < earliest) {
        earliest = day;
      }
    }

    return earliest.plus({ days }).startOf('day');
  }

  const created = datetime(order.created, order.timezone);

  if(created.hour >=2){
    return plusBusinessDays(created, 1);
  }

  return created;
}

export function bre_print_speed(order: BreOrderContext, needed?: TDateISODate | DateTime): BrePrintSpeed {
  if (!needed) {
    return BrePrintSpeed.STANDARD;
  }

  const neededDt = datetime(needed, order.timezone);
  const isRush = (compare: DateTime) => compare.hasSame(neededDt, 'day');

  for (const appointment of order.appointments) {
    const photos = order.jobs.find((j) => appointment.job_ids.includes(j.id) && j.performable_id === 'photos');

    if (photos) {
      if (isRush(datetime(appointment.start, order.timezone).plus({ day: 1 }))) {
        return BrePrintSpeed.RUSH;
      }

      // if photos exist on the order, always use it as the reference point
      return BrePrintSpeed.STANDARD;
    }
  }

  for (const requested of order.requested) {
    // during order wizard there are no official appointments, so we estimate with requested date
    if (isRush(datetime(requested.day, order.timezone).plus({ day: 1 }))) {
      return BrePrintSpeed.RUSH;
    }
  }

  // otherwise consider it a rush if needed by same day as order
  return isRush(datetime(order.created, order.timezone)) ? BrePrintSpeed.RUSH : BrePrintSpeed.STANDARD;
}

export function deliveryFee(time: number, fee: 'rate' | 'pay'): string | undefined {
  const mins = time / 60;
  const limit = BRE_DELIVERY_FEES.find(({ upTo }) => mins <= upTo);

  return limit?.[fee];
}

export function isMarketingOrder(type: BreOrderType): boolean {
  return type === BreOrderType.AGENT_MKTG || type === BreOrderType.PROPERTY_MKTG;
}

export function plusBusinessDays(datetime: DateTime, days: number) {
  const addDay = (next: DateTime, counter: number = 0): DateTime => {
    if (counter < days) {
      const nextDay = next.plus({ day: 1 });
      const nextCounter = isBusinessDay(nextDay) ? counter + 1 : counter;

      return addDay(nextDay, nextCounter);
    }

    return next;
  };

  return addDay(datetime);
}

export function isBusinessDay(datetime: DateTime): boolean {
  const holidays = holiday_lookup(datetime);
  const isHoliday = BRE_HOLIDAYS.some((day) => holidays.includes(day));
  const isWeekend = datetime.weekday > 5;

  return !isHoliday && !isWeekend;
}

export function bre_is_copyright(order: BreOrderType): boolean {
  return [BreOrderType.RENTAL, BreOrderType.LONG_TERM].includes(order);
}

export function bre_round(amount: Big) {
  // ceil to the nearest $5
  return amount.div(5).round(0, 3).times(5);
}

export function bre_increase_residential(amount: Big) {
  return amount.times('1.05');
}

export function bre_increase_commercial(amount: Big) {
  return amount.times('1.30');
}

export function bre_increase(order: BreOrderType, amount: Big) {
  // 5% increase for residential in new system
  let increased = bre_increase_residential(amount);

  if (order === BreOrderType.COMMERCIAL) {
    // 30% increase for all commercial orders
    increased = bre_increase_commercial(increased);
  }

  return increased;
}

export function bre_split_photo(amount: Big): Big {
  return bre_round(amount.times('0.65'));
}

export function bre_split_video(amount: Big, mpost = true): Big {
  // if photographer does not self edit and sends to mpost for editing, reduce their cut
  return bre_round(amount.times(mpost ? '0.50' : '0.70'));
}
